import { getUserIntegrations } from '@api';
import { Professional, ProfessionalBaseType, ResourceList } from '@domain/types';
import { useUserIdentities } from '@launcher/hooks/useUserIdentities';
import { sortUserIdentities } from '@launcher/utils';
import { logger } from '@services/logger/logger.service';
import {
  authenticateUserFailure,
  fetchIdentitiesFailure,
  fetchIdentitiesSuccess,
  fetchIntegrationsFailure,
  fetchIntegrationsSuccess,
} from '@store/user/user.actions';
import { getIsAuthenticated, getIsLifenSendingActivated } from '@store/user/user.selector';
import { AuthenticationError } from '@store/user/user.state';
import { filterSenderIdentities } from '@utils/identities.util';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

export const UserIdentitiesProvider = ({ children }: { children: React.ReactNode }) => {
  const dispatch = useDispatch();
  const isLifenSendingActivated = useSelector(getIsLifenSendingActivated);
  const isAuthenticated = useSelector(getIsAuthenticated);
  const [isFetchingIdentitiesEnabled, setIsFetchingIdentitiesEnabled] = useState(false);
  const { data: identities } = useUserIdentities(isFetchingIdentitiesEnabled);

  /*
  Identities are available, dispatch them to redux store
  */
  useEffect(() => {
    if (!identities) return;

    try {
      const hasIntegrationIdentity = identities?.list?.some((identity) => {
        const hasActiveIntegrationsModule = Boolean(
          identity.featureSets?.lifenFeatureSetIntegrations,
        );

        return identity.type === ProfessionalBaseType.Organization && hasActiveIntegrationsModule;
      });

      if (!isLifenSendingActivated && !hasIntegrationIdentity) {
        dispatch(authenticateUserFailure(AuthenticationError.MisconfiguredAccount));
        logger.info('LOGIC', 'No active applications to display');
        return;
      }

      if (identities?.total === 0 || filterSenderIdentities(identities?.list ?? []).length === 0)
        dispatch(authenticateUserFailure(AuthenticationError.MisconfiguredAccount));
      else {
        dispatch(fetchIdentitiesSuccess(sortUserIdentities(identities?.list ?? [])));
        logger.info('LOGIC', 'User identities fetched', { total: identities?.total });
      }
    } catch (error) {
      logger.error('LOGIC', 'Failed to fetch user identities', error);
      dispatch(fetchIdentitiesFailure({ error })); // not handled https://honestica.atlassian.net/browse/REB-410
    }
  }, [identities]);

  /*
  Fetch loggued user integrations from backend and dispatch them to store
  */
  const fetchIntegrations = useCallback(async () => {
    try {
      const { list: integrations, total }: ResourceList<Professional> = await getUserIntegrations();
      dispatch(fetchIntegrationsSuccess(integrations));
      logger.info('LOGIC', 'User integrations fetched', { total });
    } catch (error) {
      logger.error('LOGIC', 'Failed to fetch user integrations', error);
      dispatch(fetchIntegrationsFailure({ error }));
      // not handled https://honestica.atlassian.net/browse/REB-410
    }
  }, [dispatch]);

  /*
  Do nothing if we are not authenticated, or in the webview
  */
  useEffect(() => {
    if (isAuthenticated) {
      setIsFetchingIdentitiesEnabled(true);
      fetchIntegrations();
    }
  }, [fetchIntegrations, isAuthenticated]);

  return <>{children}</>;
};
